import { changePassword, ChangePasswordPayload } from "@/api/endpoints/user";
import { ROUTES } from "@/routes_new";
import { Organization } from "@/types/Organization";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, SubmitHandler, useFormState } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";

export default function ChangePassword() {
  const { t } = useLingui();

  // URL state
  const history = useHistory();
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // Validation
  const formSchema = z
    .object({
      password: z.string().min(1, {
        message: t`Vänligen ange ditt nuvarande lösenord`,
      }),
      new_password: z.string().min(8, {
        message: t`Lösenordet måste vara minst 8 tecken`,
      }),
      new_password_confirm: z.string().min(1, {
        message: t`Vänligen bekräfta ditt nya lösenord`,
      }),
    })
    .refine((data) => data.new_password === data.new_password_confirm, {
      message: t`Lösenorden matchar inte`,
      path: ["new_password_confirm"],
    });

  // Form state
  const form = useFormCompat<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      new_password: "",
      new_password_confirm: "",
    },
  });

  const formState = useFormState({ control: form.control });

  // Handlers
  function handleOpenChange(open: boolean) {
    if (!open) {
      setIsOpen(false);
      setTimeout(() => {
        history.push(generatePath(ROUTES.SETTINGS_ORGID, { orgUuid }));
      }, 300);
    }
  }

  // Server state
  const { mutateAsync: mutateChangePassword } = useMutation({
    mutationFn: (values: ChangePasswordPayload) => {
      return changePassword(values);
    },
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      toast.success(t`Lösenordet har uppdaterats`);
      handleOpenChange(false);
    },
    onError: () => {
      toast.error(t`Kunde inte uppdatera lösenordet`);
    },
  });

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = (values) => {
    return mutateChangePassword({
      password: values.password,
      new_password: values.new_password,
      new_password_confirm: values.new_password_confirm,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Uppdatera lösenord</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Ange ditt nuvarande lösenord och välj ett nytt.</Trans>
          </DialogDescription>
        </DialogHeader>

        <form
          id="change-password-form"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FieldGroup>
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "current-password";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Nuvarande lösenord</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      id={id}
                      type="password"
                      aria-invalid={fieldState.invalid}
                      autoComplete="current-password"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="new_password"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "new-password";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Nytt lösenord</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      id={id}
                      type="password"
                      aria-invalid={fieldState.invalid}
                      autoComplete="new-password"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />

            <Controller
              name="new_password_confirm"
              control={form.control}
              render={({ field, fieldState }) => {
                const id = "confirm-password";
                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={id}>
                      <Trans>Bekräfta nytt lösenord</Trans>
                    </FieldLabel>
                    <Input
                      {...field}
                      id={id}
                      type="password"
                      aria-invalid={fieldState.invalid}
                      autoComplete="new-password"
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={formState.isSubmitting}
            onClick={() => handleOpenChange(false)}
          >
            <Trans>Avbryt</Trans>
          </Button>

          <Button
            type="submit"
            form="change-password-form"
            disabled={formState.isSubmitting || !formState.isDirty}
          >
            <Trans>Uppdatera</Trans>
            {formState.isSubmitting && <Spinner />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
